import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { CContainer, CRow, CCol, CCard, CCardHeader, CCardBody, CSpinner, CButton } from '@coreui/react';
import api from '../../api/axiosInstance';
import MathRenderer from '../../components/MathRenderer';
import swal from 'sweetalert2';

const NotesPage = () => {
  const { section, title } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [notesInfo, setNotesInfo] = useState({ id: '', title: '', noteTitle: '', description: '', categoryId: null });
  const [chapters, setChapters] = useState([]);
  const [selectedChapterId, setSelectedChapterId] = useState(null);
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem('authToken');
  const userId = localStorage.getItem('ID') || localStorage.getItem('memberId');

  useEffect(() => {
    const fetchNotesDetails = async () => {
      setLoading(true);
      try {
        // Fetch Notes Details (Hamesha public load hone do)
        const res = await api.get(`/api/notes/GetNotesBySectionAndTitle/${section}/${title}`, { skipAuthCheck: true });
        const data = res.data || {};

        setNotesInfo({
          id: data.id || data.noteId || '',
          title: data.title || (title ? title.replace(/-/g, ' ') : ''),
          noteTitle: data.noteTitle || '',
          description: data.description || '',
          categoryId: data.categoryId || data.CategoryId || data.category_id || null
        });

        const chaptersList = data.chapters || [];
        setChapters(chaptersList);

        if (chaptersList.length > 0) {
          setSelectedChapterId(chaptersList[0].id);
          setTopics(chaptersList[0].topics || []);
        }
      } catch (err) {
        console.error('Error fetching notes details:', err);
      } finally {
        setLoading(false);
      }
    };

    if (section && title) {
      fetchNotesDetails();
    }
  }, [section, title]);

  const handleChapterClick = (chapter) => {
    setSelectedChapterId(chapter.id);
    setTopics(chapter.topics || []);
  };

  // Topic Click par Subscription Verification Logic
  const handleTopicClick = async (topicName) => {
    const currentSection = String(section || '').toLowerCase();
    const isAlphaSection = currentSection.includes('alpha');

    // 1. Login Check
    if (!token) {
      navigate('/login', { state: { from: location.pathname } });
      return;
    }

    // 2. Section/Category Subscription Check
    if (!isAlphaSection) {
      try {
        const subRes = await api.get(`/api/members/${userId}/subscription`);
        const subData = subRes.data?.data || subRes.data;
        const userSubs = Array.isArray(subData) ? subData : subData ? [subData] : [];

        // Active subscriptions se categoryIds collect karein (e.g. "7,8,27" ya "13")
        const allowedCategoryIds = userSubs.flatMap((sub) => {
          const ids = sub.categoryIds || sub.CategoryIds || sub.categoryId || sub.CategoryId || '';
          return String(ids).split(',').map((catId) => catId.trim());
        });

        // Current Note ki categoryId
        const noteCatId = notesInfo.categoryId;
        const noteCatIds = Array.isArray(noteCatId)
          ? noteCatId.map((id) => String(id).trim())
          : noteCatId !== undefined && noteCatId !== null
            ? String(noteCatId).split(',').map((id) => id.trim())
            : [];

        // Check availability in user subscriptions
        const isSubscribed = noteCatIds.length > 0 && noteCatIds.some((id) => allowedCategoryIds.includes(id));

        if (!isSubscribed) {
          swal.fire({
            title: "Subscription Required",
            text: "To view this topic, please subscribe to this category.",
            icon: "warning",
            confirmButtonText: "Subscribe Now",
            showCancelButton: true,
            cancelButtonText: "Cancel",
            allowOutsideClick: false
          }).then((result) => {
            if (result.isConfirmed) {
              navigate('/profile', { state: { from: location.pathname } });
            }
          });
          return;
        }
      } catch (subErr) {
        console.error("Subscription check failed:", subErr);
        navigate('/profile');
        return;
      }
    }

    // 3. Subscription Verified -> Navigate to Topic Page
    const chapterName = chapters.find((ch) => ch.id === selectedChapterId)?.name || 'chapter';
    navigate(
      `/${section}/${title}/notes/${notesInfo.id}/${encodeURIComponent(chapterName.trim().replace(/\s+/g, '-'))}/${encodeURIComponent(topicName.trim().replace(/\s+/g, '-'))}`
    );
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <CSpinner color="primary" />
        <p className="mt-2 fw-semibold">Loading Notes Details...</p>
      </div>
    );
  }

  return (
    <div className="pb-5 bg-light min-vh-100">
      {/* Top Banner */}
      <div style={{ backgroundColor: 'rgb(0, 33, 71)', color: 'white', padding: '40px 0' }}>
        <CContainer className="text-center">
          <h1 className="fw-bold mb-0 text-capitalize" style={{ color: 'yellow' }}>{notesInfo.title}</h1>
        </CContainer>
      </div>

      <CContainer className="mt-4">
        {/* Note Title & Description */}
        {(notesInfo.noteTitle || notesInfo.description) && (
          <CCard className="shadow-sm border-0 mb-4">
            <CCardBody className="p-4">
              {notesInfo.noteTitle && (
                <h3 className="fw-bold text-dark border-bottom pb-2 mb-3">
                  {notesInfo.noteTitle}
                </h3>
              )}
              {notesInfo.description && (
                <div className="note-html-content text-secondary fs-6">
                  <MathRenderer htmlContent={notesInfo.description} />
                </div>
              )}
            </CCardBody>
          </CCard>
        )}

        {/* Chapters & Topics Section */}
        <CRow className="g-4">
          <CCol md={4}>
            <CCard className="shadow-sm border-dark">
              <CCardHeader className="bg-warning text-white fw-bold d-flex justify-content-between align-items-center">
                <span>Chapters</span>
                <span className="badge bg-light text-primary">{chapters.length}</span>
              </CCardHeader>
              <CCardBody className="p-0">
                {chapters.length === 0 ? (
                  <div className="p-3 text-muted text-center">No chapters found for these notes.</div>
                ) : (
                  <ul className="list-group list-group-flush">
                    {chapters.map((ch) => (
                      <li
                        key={ch.id}
                        onClick={() => handleChapterClick(ch)}
                        style={{ cursor: 'pointer' }}
                        className={`list-group-item d-flex justify-content-between align-items-center fw-semibold py-3 ${selectedChapterId === ch.id ? 'active' : ''}`}
                      >
                        <span>{ch.name}</span>
                        <span className="badge bg-secondary rounded-pill">
                          {ch.topics?.length || 0}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </CCardBody>
            </CCard>
          </CCol>

          <CCol md={8}>
            <CCard className="shadow-sm border-dark">
              <CCardHeader className="bg-white text-dark fw-bold border-bottom">
                Topics
              </CCardHeader>
              <CCardBody>
                {topics.length === 0 ? (
                  <div className="text-muted p-4 text-center">
                    Select a chapter from the left side to view its topics.
                  </div>
                ) : (
                  <ul className="list-group list-group-flush">
                    {topics.map((topic, index) => {
                      const topicName = topic.name || topic;

                      return (
                        <li
                          key={topic.id || index}
                          className="list-group-item d-flex align-items-center gap-2 border-bottom py-3"
                          onClick={() => handleTopicClick(topicName)}
                          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#e9ecef')}
                          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#ffffff')}
                          style={{
                            cursor: 'pointer',
                            backgroundColor: '#ffffff',
                            transition: 'background-color 0.2s ease-in-out'
                          }}
                        >
                          <span className="text-primary fw-bold">{index + 1}.</span>
                          <span className="fw-medium text-dark">{topicName}</span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>

        <div className="text-center mt-4">
          <CButton color="secondary" onClick={() => navigate(-1)}>
            Go Back
          </CButton>
        </div>
      </CContainer>
    </div>
  );
};

export default NotesPage;